import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TypingTest from '../components/TypingTest';
import { isMobileDevice } from '../utils/deviceDetection';
import { Shell } from '../components/Shell';
import { DesktopRequired } from '../components/DesktopRequired';
import { ArrowLeftIcon, InfoIcon } from '../components/Icons';
import { authHeaders, CodeState, draftKey, fetchCodeState, formatTimeLeft, useTimeLeft } from '../lib/tries';

type Phase = 'checking' | 'recovering' | 'ready' | 'error';

const Test: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [isMobile] = useState(() => isMobileDevice());
  const [phase, setPhase] = useState<Phase>('checking');
  const [state, setState] = useState<CodeState | null>(null);
  const [attempt, setAttempt] = useState(0);
  const timeLeft = useTimeLeft(phase === 'ready' ? state?.expires_at : null);

  // The hour ran out before they started this try: their best score has been submitted
  useEffect(() => {
    if (timeLeft === 0 && code && !localStorage.getItem(draftKey(code))) {
      navigate(`/results/${code}`, { replace: true });
    }
  }, [timeLeft, code, navigate]);

  useEffect(() => {
    if (!code || isMobile) return;

    const storedCode = sessionStorage.getItem('userCode');
    if (!storedCode || storedCode !== code) {
      navigate('/');
      return;
    }

    let cancelled = false;

    // A try interrupted by a refresh or closed tab is submitted as it stood
    const submitDraft = async (): Promise<boolean> => {
      const draftStr = localStorage.getItem(draftKey(code));
      if (!draftStr) return false;

      let draft: any;
      try {
        draft = JSON.parse(draftStr);
      } catch {
        localStorage.removeItem(draftKey(code));
        return false;
      }

      // Ignore stale drafts older than 24 hours
      if (Date.now() - draft.savedAt > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(draftKey(code));
        return false;
      }

      setPhase('recovering');
      try {
        const response = await axios.post('/api/test/submit', draft.payload, { timeout: 15000, headers: authHeaders() });
        if (response.data.success) {
          localStorage.removeItem(draftKey(code));
          sessionStorage.setItem('testResults', JSON.stringify({
            ...response.data.metrics,
            _submitted: true,
          }));
          navigate(`/results/${code}`, { replace: true });
          return true;
        }
      } catch (e: any) {
        // Refused (no tries left or out of order): the draft can never be saved
        if (e?.response?.status === 409) localStorage.removeItem(draftKey(code));
        console.error('Draft recovery submission failed:', e);
      }
      return false;
    };

    const load = async () => {
      if (await submitDraft()) return;
      try {
        const next = await fetchCodeState();
        if (cancelled) return;
        if (!next.open || !next.next_try) {
          // Nothing left to take: show what they did (or send them back to enter a code)
          navigate(next.tries.length > 0 ? `/results/${code}` : '/', { replace: true });
          return;
        }
        setState(next);
        setPhase('ready');
      } catch {
        if (!cancelled) setPhase('error');
      }
    };

    load();
    return () => { cancelled = true; };
  }, [code, navigate, isMobile, attempt]);

  const handleTestComplete = (results: any) => {
    sessionStorage.setItem('testResults', JSON.stringify(results));
    navigate(`/results/${code}`);
  };

  if (isMobile) {
    return (
      <Shell>
        <div className="card entry-card" style={{ maxWidth: 440 }}>
          <DesktopRequired>
            <button type="button" className="btn btn-secondary" style={{ marginTop: 24 }} onClick={() => navigate('/')}>
              <ArrowLeftIcon size={16} />
              Go Back
            </button>
          </DesktopRequired>
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell brandLink={false}>
        <div className="card entry-card" style={{ maxWidth: 440, textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: 10 }}>Couldn't Load Your Test</h2>
          <p style={{ color: 'var(--ink-2)', lineHeight: 1.6, fontSize: '0.95rem' }}>
            We couldn't reach the server. Check your internet connection and try again.
          </p>
          <button type="button" className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => { setPhase('checking'); setAttempt(a => a + 1); }}>
            Try Again
          </button>
        </div>
      </Shell>
    );
  }

  if (phase !== 'ready' || !state || !state.next_try || !code) {
    return (
      <Shell brandLink={false}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading" />
          {phase === 'recovering' && <p style={{ color: 'var(--ink-2)', marginTop: 16 }}>Submitting your results…</p>}
        </div>
      </Shell>
    );
  }

  const tryNumber = state.next_try;
  const best = state.tries.find(t => t.try_number === state.best_try);
  const triesLeftAfter = state.max_tries - tryNumber;

  return (
    <Shell brandLink={false}>
      <div className="page-narrow">
        <div className="page-header">
          <h1>Typing Test</h1>
          <div className="page-sub">
            <span className="chip">Code <strong>{code}</strong></span>
            <span className="chip brand">Try {tryNumber} of {state.max_tries}</span>
            <span className="chip">60 Seconds</span>
          </div>
        </div>

        <TypingTest
          key={tryNumber}
          identifier={code}
          tryNumber={tryNumber}
          onComplete={handleTestComplete}
          isPractice={false}
          notice={
            <div className="notice brand">
              <InfoIcon size={16} />
              <span>
                {tryNumber === 1 ? (
                  <>
                    You have <strong>{state.max_tries} tries</strong>, each on a new passage, and your <strong>best score</strong> is the one that counts.
                    After your first try you have 1 hour to use the rest. The timer starts when you start typing.
                  </>
                ) : (
                  <>
                    Your best so far is <strong>{best?.adjusted_wpm ?? 0} Net WPM</strong> (Try {state.best_try}).{' '}
                    {triesLeftAfter === 0 ? 'This is your last try.' : `After this you'll have ${triesLeftAfter} more`}
                    {triesLeftAfter > 0 && timeLeft !== null && <>, with <strong>{formatTimeLeft(timeLeft)}</strong> left to use {triesLeftAfter === 1 ? 'it' : 'them'}</>}
                    {triesLeftAfter > 0 && '.'} The timer starts when you start typing.
                  </>
                )}
              </span>
            </div>
          }
        />
      </div>
    </Shell>
  );
};

export default Test;
